import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { templates } from '@repletoryreach/sdk-api';
import { useDebounce } from '@repletoryreach/sdk-utils/hooks';
import { Stack, Card, Group, Button, Text, Badge, ActionIcon } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, DataTable, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Menu, Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { TextInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { HelpButton } from '../../app/guides/HelpButton';
import { useT } from '../../i18n/I18nProvider';
import { formatDateTime, formatCount, type Indexed } from '../../lib/format';
import {
  Banner, Filter, SearchField, StatusPill, StatusDot, RowName,
  FilterIcon, PlusIcon, type PillTone
} from '../../components/panel';
import { usePersistedState } from '../../lib/usePersistedState';
import { useTemplateRealtimeSync } from '../../data/useTemplateEvent';
import { MetaRefusalNotice, refusalFrom, type Refusal } from '../numbers/MetaRefusalNotice';
import './templates.css';

/** Status codes, in lifecycle order. */
const STATUSES = ['draft', 'submitted', 'approved', 'rejected', 'paused'] as const;

export default function TemplatesPage() {
  const nav = useNavigate();
  const t = useT();
  const qc = useQueryClient();
  const [status, setStatus] = usePersistedState('tenant.templates.status', '');
  const [query, setQuery] = useState('');
  const search = useDebounce(query, 250);

  /*
   * §4 item 4: channel-wa writes `template.approved` / `template.rejected` to
   * its outbox, the inbox service fans them out over `/hubs/inbox`, and
   * sdk-realtime types them: a complete pipeline the tenant console did not
   * subscribe to, so a template approved by Meta stayed "submitted" on screen
   * until someone reloaded the page.
   */
  useTemplateRealtimeSync();

  const { data, isPending, error } = useQuery({
    queryKey: ['templates'],
    queryFn: () => templates.list()
  });

  const all = (data?.data ?? []) as Indexed<templates.MessageTemplate>[];

  // `templates.list()` returns everything, so filtering is client-side by
  // necessity; with sixty templates the unfiltered list was unnavigable.
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((tpl) => {
      if (status && tpl.status !== status) return false;
      if (!q) return true;
      return tpl.code.toLowerCase().includes(q)
        || tpl.language.toLowerCase().includes(q)
        || (tpl.category ?? '').toLowerCase().includes(q);
    });
  }, [all, status, search]);

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const tpl of all) map.set(tpl.status, (map.get(tpl.status) ?? 0) + 1);
    return map;
  }, [all]);

  // WhatsApp's refusal of a submit, in the shared shape. The common one is a
  // number on this account still signed in to the WhatsApp Business app on a
  // phone, which Meta reports as "not allowed to create or update templates";
  // the notice says so and carries the button into the guided move.
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const submit = useMutation({
    mutationFn: (tpl: templates.MessageTemplate) => templates.submit(tpl.id),
    onSuccess: (_r, tpl) => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      setRefusal(null);
      notifySuccess(t('templates.toast.submittedNamed', { code: tpl.code }), t('templates.title'));
    },
    onError: (e) => {
      const r = refusalFrom(e);
      if (r) { setRefusal(r); return; }
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('templates.toast.submitFailed'));
    }
  });

  const del = useMutation({
    mutationFn: (tpl: templates.MessageTemplate) => templates.remove(tpl.id),
    // §4 item 3: silent on success AND on failure.
    onSuccess: (_r, tpl) => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      notifySuccess(t('templates.toast.deleted', { code: tpl.code }), t('toast.deleted'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('templates.toast.deleteFailed'))
  });

  /*
   * §4 item 5: `POST /v1/templates/import-from-meta` existed with no button,
   * so first-run onboarding for any org with an existing WABA was "retype your
   * catalogue". The review called it "the cheapest high-value change in the
   * whole slice".
   */
  const importMeta = useMutation({
    mutationFn: () => templates.importFromMeta(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      notifySuccess(
        t('templates.toast.imported', {
          imported: res.data.imported,
          updated: res.data.updated,
          skipped: res.data.skipped
        }),
        t('templates.title')
      );
    },
    onError: (e) => notifyError(importError(e, t), t('templates.toast.importFailed'))
  });

  function confirmSubmit(tpl: templates.MessageTemplate) {
    // §4 item 10: the detail page confirmed and the list did not, though
    // submitting is the one-way door: the template becomes immutable and
    // enters a queue that takes a day or more.
    openConfirmModal({
      title: t('templates.modal.submit.title'),
      children: (
        <Stack gap={8}>
          <Text role="bodySmall">{t('templates.modal.submit.body', { code: tpl.code })}</Text>
          <Text role="caption" c="var(--rr-color-text-muted)">{t('templates.modal.submit.irreversible')}</Text>
        </Stack>
      ),
      labels: { confirm: t('templates.actions.submit'), cancel: t('action.cancel') },
      onConfirm: () => submit.mutate(tpl)
    });
  }

  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilterCount = (search.trim() ? 1 : 0) + (status ? 1 : 0);

  /** Templates whose last submit came back rejected: the banner's subject. */
  const rejected = all.filter((tpl) => tpl.status === 'rejected');

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('templates.title')}
        description={t('templates.description')}
        withDivider={false}
        actions={<HelpButton guide="automation" />}
      />

      {error && <Alert color="error" role="alert">{t('templates.loadError')}</Alert>}
      {refusal && <MetaRefusalNotice refusal={refusal} onClose={() => setRefusal(null)} />}

      {/* The rule that governs this whole page, said once. A template is not
          usable until Meta says so, and nothing on the page said that. */}
      <div className="rr-templates__intro">
        <Banner tone="info">{t('templates.banner')}</Banner>
        <Button
          size="sm"
          leftSection={<PlusIcon />}
          data-guide="templates-new"
          onClick={() => nav('/templates/new')}
        >
          {t('templates.actions.create')}
        </Button>
      </div>

      {/* A rejection is the one state that needs acting on, and it was a cell
          in a column people do not read. */}
      {rejected.length > 0 && (
        <Banner
          tone="error"
          action={
            <button type="button" className="rr-panel__clear" onClick={() => setStatus('rejected')}>
              {t('templates.banner.rejected.action')}
            </button>
          }
        >
          {t('templates.banner.rejected', { count: rejected.length })}
        </Banner>
      )}

      <Card withBorder noPadding className="rr-panel" data-guide="templates-list">
        <div className="rr-panel__filters">
          <Filter label={t('templates.search.label')}>
            <SearchField>
              <TextInput
                type="search"
                className="rr-panel__search-input"
                placeholder={t('templates.search.placeholder')}
                value={query}
                onChange={(e) => setQuery(e.currentTarget.value)}
              />
            </SearchField>
          </Filter>

          <Filter label={t('common.status')}>
            <Select
              value={status}
              onChange={(e) => setStatus(e.currentTarget.value)}
              data={[
                { value: '', label: t('templates.filters.status.any', { count: all.length }) },
                ...STATUSES.map((s) => ({
                  value: s,
                  label: `${t(`templates.status.${s}`, s)} (${counts.get(s) ?? 0})`
                }))
              ]}
            />
          </Filter>

          <div className="rr-panel__filter-actions">
            {activeFilterCount > 0 && (
              <button type="button" className="rr-panel__clear" onClick={() => { setStatus(''); setQuery(''); }}>
                {t('contacts.filters.clear')}
              </button>
            )}
            <button
              type="button"
              className={`rr-panel__filterbtn${activeFilterCount > 0 ? ' is-active' : ''}`}
              aria-pressed={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
            >
              <FilterIcon />
              {t('contacts.filters.title')}
              {activeFilterCount > 0 && <span className="rr-panel__filterbtn-count">{activeFilterCount}</span>}
            </button>
          </div>
        </div>

        {!isPending && rows.length === 0 ? (
          <div style={{ padding: '24px 18px' }}>
            <EmptyState
              title={all.length === 0 ? t('templates.empty') : t('templates.emptyFiltered')}
              description={all.length === 0 ? t('templates.empty.hint') : t('templates.emptyFiltered.hint')}
              action={
                all.length === 0
                  ? (
                    <Group gap="xs">
                      <Button size="xs" onClick={() => nav('/templates/new?from=library')}>
                        {t('templates.ways.library.action')}
                      </Button>
                      <Button size="xs" variant="default" loading={importMeta.isPending} onClick={() => importMeta.mutate()}>
                        {t('templates.actions.importFromMeta')}
                      </Button>
                      <Button size="xs" variant="subtle" onClick={() => nav('/templates/new?from=blank')}>
                        {t('templates.ways.blank.action')}
                      </Button>
                    </Group>
                  )
                  : <Button size="xs" variant="default" onClick={() => { setStatus(''); setQuery(''); }}>{t('contacts.filters.clear')}</Button>
              }
            />
          </div>
        ) : (
          <DataTable<Indexed<templates.MessageTemplate>>
            records={rows}
            loading={isPending}
            idAccessor="id"
            onRowClick={(tpl) => nav(`/templates/${tpl.id}`)}
            emptyState={t('templates.empty')}
            columns={[
              {
                accessor: 'code',
                title: t('templates.columns.code'),
                render: (tpl) => (
                  <RowName
                    icon={<TemplateIcon />}
                    tint={TINT[tpl.status] ?? 'var(--rr-color-text-muted)'}
                    title={<code className="rr-templates__code">{tpl.code}</code>}
                  />
                )
              },
              {
                accessor: 'category',
                title: t('templates.columns.category'),
                render: (tpl) => t(`templates.category.${tpl.category}`, tpl.category ?? '')
              },
              { accessor: 'language', title: t('common.language') },
              {
                accessor: 'status',
                title: t('common.status'),
                render: (tpl) => (
                  <Group gap={4} wrap="nowrap">
                    <StatusPill tone={statusTone(tpl.status)}>
                      {t(`templates.status.${tpl.status}`, tpl.status)}
                    </StatusPill>
                    {/* §4 item 6: a user in a non-production environment saw
                        "approved" and reasonably assumed Meta had approved it. */}
                    {tpl.dev_fallback && (
                      <Tooltip label={t('templates.devFallback.tooltip')} position="top">
                        <Badge size="xs" color="blue" variant="light">{t('templates.devFallback')}</Badge>
                      </Tooltip>
                    )}
                    {/* §4 item 7: "submitted" (our word) and PENDING (Meta's)
                        are not the same claim, and divergence was invisible. */}
                    {tpl.meta_status && tpl.meta_status.toLowerCase() !== tpl.status && (
                      <Tooltip label={t('templates.metaStatus.tooltip', { status: tpl.meta_status })} position="top">
                        <Badge size="xs" variant="light">{tpl.meta_status}</Badge>
                      </Tooltip>
                    )}
                  </Group>
                )
              },
              {
                accessor: 'rejection_reason',
                title: t('templates.columns.note'),
                /*
                 * The note column answers "why is it in that state?", which is
                 * the only question a status raises. A rejection reason is the
                 * loudest case, but a submitted template is waiting on Meta and
                 * a dev-fallback approval was never seen by Meta at all, and
                 * both were silent here.
                 */
                render: (tpl) => {
                  const note = tpl.rejection_reason
                    ?? (tpl.dev_fallback ? t('templates.note.devFallback')
                      : tpl.status === 'submitted' ? t('templates.note.underReview')
                      : tpl.status === 'paused' ? t('templates.note.paused')
                      : null);
                  return note
                    ? (
                      <Tooltip label={note} position="top">
                        <span className="rr-templates__note">{note}</span>
                      </Tooltip>
                    )
                    : <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text>;
                }
              },
              { accessor: 'updated_at', title: t('common.updated'), render: (tpl) => formatDateTime(tpl.updated_at) },
              {
                accessor: 'actions',
                title: t('common.actions'),
                render: (tpl) => (
                  <div className="rr-rowactions" onClick={(e) => e.stopPropagation()}>
                    {(tpl.status === 'draft' || tpl.status === 'rejected') && (
                      <>
                        <Tooltip label={t('action.edit')} position="top">
                          <ActionIcon
                            variant="default"
                            size="sm"
                            aria-label={t('action.edit')}
                            onClick={() => nav(`/templates/${tpl.id}/edit`)}
                          >
                            <PencilIcon />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t('templates.actions.submit')} position="top">
                          <ActionIcon
                            variant="default"
                            size="sm"
                            aria-label={t('templates.actions.submit')}
                            onClick={() => confirmSubmit(tpl)}
                          >
                            <SendIcon />
                          </ActionIcon>
                        </Tooltip>
                      </>
                    )}

                    <Menu>
                      <Menu.Target>
                        <ActionIcon variant="subtle" size="sm" aria-label={t('templates.actions.for', { code: tpl.code })}>
                          <MoreIcon />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => nav(`/templates/${tpl.id}`)}>{t('action.open')}</Menu.Item>
                        {/* §4 item 12: a second language meant retyping the
                            whole components block. */}
                        <Menu.Item onClick={() => nav(`/templates/new?clone=${tpl.id}`)}>
                          {t('templates.actions.duplicate')}
                        </Menu.Item>
                        {tpl.status !== 'approved' && (
                          <>
                            <Menu.Divider />
                            <Menu.Item
                              color="red"
                              onClick={() => openConfirmModal({
                                title: t('templates.modal.delete.title'),
                                children: <Text role="bodySmall">{t('templates.modal.delete.body', { code: tpl.code })}</Text>,
                                labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
                                confirmProps: { color: 'red' },
                                onConfirm: () => del.mutate(tpl)
                              })}
                            >
                              {t('action.delete')}
                            </Menu.Item>
                          </>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </div>
                )
              }
            ]}
          />
        )}

        {rows.length > 0 && (
          <div className="rr-panel__foot">
            <span>{t('templates.showingOf', { count: formatCount(rows.length), total: formatCount(all.length) })}</span>
            {/* A legend, so a colour on a row has somewhere to be looked up
                rather than guessed at. */}
            <span className="rr-panel__legend">
              {STATUSES.map((sCode) => (
                <StatusDot key={sCode} tone={statusTone(sCode)} label={t(`templates.status.${sCode}`, sCode)} />
              ))}
            </span>
          </div>
        )}
      </Card>

      {/*
        The three ways in, together and named.
        There has always been exactly one way a customer could fill this page:
        write Meta's component JSON by hand in a drawer. `import-from-meta` was
        a real endpoint with a button buried under the table, and a library did
        not exist at all, so an org whose WhatsApp account was brand new (which
        is every org on their first day) met an empty list and a schema.
      */}
      <div className="rr-templates__ways">
        <WayIn
          icon={<LibraryIcon />}
          title={t('templates.ways.library')}
          body={t('templates.ways.library.body')}
          action={t('templates.ways.library.action')}
          onClick={() => nav('/templates/new?from=library')}
        />
        <WayIn
          icon={<ImportIcon />}
          title={t('templates.ways.import')}
          body={t('templates.ways.import.body')}
          action={t('templates.actions.importFromMeta')}
          loading={importMeta.isPending}
          onClick={() => importMeta.mutate()}
        />
        <WayIn
          icon={<PencilIcon />}
          title={t('templates.ways.blank')}
          body={t('templates.ways.blank.body')}
          action={t('templates.ways.blank.action')}
          onClick={() => nav('/templates/new?from=blank')}
        />
      </div>

    </Stack>
  );
}

/**
 * One of the three ways to fill this page. A card rather than a menu item:
 * these are three different jobs, not three spellings of one, and the sentence
 * under each is what tells somebody which of them is theirs.
 */
function WayIn({
  icon, title, body, action, loading, onClick
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  action: string;
  loading?: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" className="rr-templates__way" onClick={onClick} disabled={loading}>
      <span className="rr-templates__way-icon" aria-hidden>{icon}</span>
      <span className="rr-templates__way-title">{title}</span>
      <span className="rr-templates__way-body">{body}</span>
      <span className="rr-templates__way-action">{loading ? '…' : action}</span>
    </button>
  );
}

function LibraryIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 4.5h5a2.5 2.5 0 0 1 2.5 2.5v12a2 2 0 0 0-2-2H4z" />
      <path d="M20 4.5h-5A2.5 2.5 0 0 0 12.5 7v12a2 2 0 0 1 2-2H20z" />
    </svg>
  );
}

/** Tint for the row icon, so status is legible before the pill is read. */
const TINT: Record<string, string> = {
  approved: 'var(--rr-color-success, #16a34a)',
  submitted: 'var(--rr-color-warning, #f59e0b)',
  rejected: 'var(--rr-color-error, #dc2626)',
  paused: 'var(--rr-color-warning, #f59e0b)'
};

function TemplateIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 2.4h7.4L16 6v11.6H5z" />
      <path d="M12.2 2.5v3.7h3.7" />
      <path d="M7.6 10.2h6M7.6 13.3h4" />
    </svg>
  );
}
function PencilIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M11.2 2.3 13.7 4.8 5.5 13H3v-2.5z" />
      <path d="M9.8 3.7 12.3 6.2" />
    </svg>
  );
}
function SendIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17.5 2.5 9 11" />
      <path d="M17.5 2.5 12.1 17.5 9 11 2.5 7.9z" />
    </svg>
  );
}
function MoreIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <circle cx="8" cy="3.2" r="1.35" />
      <circle cx="8" cy="8" r="1.35" />
      <circle cx="8" cy="12.8" r="1.35" />
    </svg>
  );
}
function ImportIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 15.5A4.5 4.5 0 0 1 6 7a5.5 5.5 0 0 1 10.6-1.3A4 4 0 0 1 20 13.6" />
      <path d="M12 10.5v7" />
      <polyline points="9,14.5 12,17.5 15,14.5" />
    </svg>
  );
}

/**
 * The import has three documented failure modes and each one has a different
 * fix, so "Import failed" on its own sends the user nowhere.
 */
function importError(e: unknown, t: (k: string, v?: Record<string, string | number>) => string): string {
  const code = (e as { code?: string } | null)?.code;
  if (code === 'NO_WABA_ID') return t('templates.import.noWaba');
  if (code === 'not_configured') return t('templates.import.notConfigured');
  if (code === 'META_ERROR') return t('templates.import.metaError');
  return e instanceof Error ? e.message : t('common.unknownError');
}

function statusTone(s: string): PillTone {
  switch (s) {
    case 'approved': return 'approved';
    case 'submitted': return 'submitted';
    case 'rejected': return 'rejected';
    case 'paused': return 'paused';
    default: return 'draft';
  }
}
